import type { MenuItemProps } from '@wix/thunderbolt-becky-types';
import type { Overwrite } from '@wix/editor-elements-types/utils';
import type { IBreadcrumbsMapperProps } from './Breadcrumbs.types';

type IPropsData = Pick<
  IBreadcrumbsMapperProps,
  | 'svgString'
  | 'showOnlyPreviousPageOnMobile'
  | 'showHomePageAsIcon'
  | 'showHomePage'
  | 'showCurrentPage'
>;

type IBreadcrumbsMenuItemProps = Overwrite<
  MenuItemProps,
  { link?: MenuItemProps['link']; label?: string }
> & {
  isCurrent?: boolean;
  icon?: string;
};

type IBreadcrumbsTrail = Array<IBreadcrumbsMenuItemProps>;
type IBreadcrumbsItem = Overwrite<IBreadcrumbsMenuItemProps, { link?: string }>;

export interface IPagesInfo {
  pagesMenuItems: Array<MenuItemProps>;
  isMobile: boolean;
}

interface IRouteInfo {
  currentUrl: string;
  currentPage: Record<string, any>;
  homePageHref: string;
}

const copy = <T extends Record<string, any> | Array<any>>(target: T): T =>
  JSON.parse(JSON.stringify(target));

const startsWithHomepage = (trail: IBreadcrumbsTrail, homePageHref: string) =>
  trail[0]?.link?.href === homePageHref;

const endsWithCurrent = (trail: IBreadcrumbsTrail, currentUrl: string) => {
  const lastItem = trail[trail.length - 1];

  return Boolean(lastItem?.link?.href === currentUrl || lastItem?.isCurrent);
};

const getCurrentPageBreadcrumb = (
  pagesMenuItems: IPagesInfo['pagesMenuItems'],
  routeInfo: IRouteInfo,
) => {
  const { currentUrl, currentPage } = routeInfo;
  const currentBreadcrumbItem = getMenuItemByHref(pagesMenuItems, currentUrl);

  const label =
    currentBreadcrumbItem?.label || currentPage?.title || currentPage?.name;

  return label && { label, isCurrent: true };
};

const getMenuItemByHref = (
  pagesMenuItems: IPagesInfo['pagesMenuItems'] | undefined,
  href: string,
): MenuItemProps | undefined => {
  return pagesMenuItems?.find(item => {
    return item.link?.href === href || getMenuItemByHref(item.items, href);
  });
};

const getFullBreadcrumbsTrail = (
  trail: IBreadcrumbsTrail,
  { pagesMenuItems }: IPagesInfo,
  routeInfo: IRouteInfo,
  homePageHref: string,
  mapperProps: IPropsData,
) => {
  const { currentUrl } = routeInfo;
  const { showHomePage, showCurrentPage } = mapperProps;

  const homeBreadcrumb = getHomeBreadcrumb(
    pagesMenuItems,
    currentUrl,
    homePageHref,
    mapperProps,
  );

  const currentPageBreadcrumb = getCurrentPageBreadcrumb(
    pagesMenuItems,
    routeInfo,
  );

  if (showHomePage && homeBreadcrumb) {
    trail.unshift(homeBreadcrumb);
  }

  const shouldAddCurrentPage =
    showCurrentPage && !endsWithCurrent(trail, currentUrl);

  if (shouldAddCurrentPage && currentPageBreadcrumb) {
    trail.push(currentPageBreadcrumb);
  }

  return trail;
};

const getHomeBreadcrumb = (
  pagesMenuItems: IPagesInfo['pagesMenuItems'],
  currentUrl: string,
  homePageHref: string,
  propsData: IPropsData,
) => {
  const { showHomePageAsIcon, svgString } = propsData;
  const homeBreadcrumb: IBreadcrumbsMenuItemProps | undefined =
    getMenuItemByHref(pagesMenuItems, homePageHref);

  if (homeBreadcrumb) {
    if (currentUrl === homePageHref) {
      homeBreadcrumb.isCurrent = true;
      delete homeBreadcrumb.link;
    }

    if (showHomePageAsIcon) {
      homeBreadcrumb.icon = svgString;
      delete homeBreadcrumb.label;
    }
  }

  return homeBreadcrumb;
};

const getPreviousPageOnlyTrail = (
  trail: IBreadcrumbsTrail,
  { pagesMenuItems }: IPagesInfo,
  { currentUrl }: IRouteInfo,
  homePageHref: string,
  propsData: IPropsData,
) => {
  const findPreviousPage = (items: IBreadcrumbsTrail) => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].link?.href) {
        return items[i];
      }
    }
    // We need this one, because if there is no previous page in trail,
    // we will return home page from `pagesMenuItems`
    return getHomeBreadcrumb(
      pagesMenuItems,
      currentUrl,
      homePageHref,
      propsData,
    );
  };

  const previousPageItem = trail.length
    ? findPreviousPage(trail)
    : getHomeBreadcrumb(pagesMenuItems, currentUrl, homePageHref, propsData);

  return previousPageItem ? [previousPageItem] : [];
};

const getBreadcrumbsTrail = (
  menuItems: IBreadcrumbsTrail,
  routeInfo: IRouteInfo,
  res: IBreadcrumbsTrail = [],
): IBreadcrumbsTrail => {
  for (const item of menuItems) {
    if (item.link?.href === routeInfo.currentUrl) {
      return [...res, item];
    }

    if (item.items?.length) {
      const possibleTrail = getBreadcrumbsTrail(item.items, routeInfo, [
        ...res,
        item,
      ]);

      if (possibleTrail.length) {
        return possibleTrail;
      }
    }
  }
  return [];
};

const flattenBreadcrumbsLink = (trail: IBreadcrumbsTrail) =>
  trail.map((item): IBreadcrumbsItem => {
    const { link, ...itemWithoutLink } = item;
    const flatLink = link?.href ? { link: link?.href } : {};
    return { ...itemWithoutLink, ...flatLink };
  });

export const getBreadcrumbsItems = (
  routeInfo: IRouteInfo,
  breadcrumbsPageConfig: IPagesInfo,
  propsData: IPropsData,
) => {
  const { currentUrl, homePageHref } = routeInfo;
  /**
   * Further, we will mutate data in order to build breadcrumbs,
   * so we make deep copies just not to break shared stuff
   */
  const clonedPageConfig = copy(breadcrumbsPageConfig);

  const { pagesMenuItems } = clonedPageConfig;
  const { showOnlyPreviousPageOnMobile } = propsData;

  const trail = getBreadcrumbsTrail(pagesMenuItems, routeInfo);

  // home will be taken from the pages menu if necessary
  if (startsWithHomepage(trail, homePageHref)) {
    trail.shift();
  }

  // current page will be taken from the pages menu if necessary
  if (endsWithCurrent(trail, currentUrl)) {
    trail.pop();
  }

  const getBreadcrumbs = showOnlyPreviousPageOnMobile
    ? getPreviousPageOnlyTrail
    : getFullBreadcrumbsTrail;

  const breadcrumbs = getBreadcrumbs(
    trail,
    clonedPageConfig,
    routeInfo,
    homePageHref,
    propsData,
  );

  return flattenBreadcrumbsLink(breadcrumbs);
};
